"use client";
import { useEffect } from "react";

type Props = {
  analyticsId?: string;
  token: string;
  site: Site;
};

// Starts once the page has loaded, as the old `lazyOnload` script did, and
// loads the agent as its own chunk so it stays off the critical path.
const CashierAgentClient: FC<Props> = ({ analyticsId, token, site }) => {
  useEffect(() => {
    let isCancelled = false;
    let stopPolling: (() => void) | null = null;

    const start = async () => {
      // Tracking is best-effort: a failed start, e.g. a ChunkLoadError in a
      // tab still open on the previous build, must not surface as an
      // unhandled rejection.
      try {
        const { default: cashierAgent } = await import(
          "@workspace/cashier-agent"
        );
        if (isCancelled) {
          return;
        }
        stopPolling = await cashierAgent({ token, analyticsId, brand: site });
        if (isCancelled) {
          stopPolling?.();
        }
      } catch {
        // Ignored; see above.
      }
    };

    if (document.readyState === "complete") {
      start();
    } else {
      window.addEventListener("load", start, { once: true });
    }

    return () => {
      isCancelled = true;
      window.removeEventListener("load", start);
      stopPolling?.();
    };
  }, [analyticsId, token, site]);

  return null;
};

export default CashierAgentClient;
